import React from 'react';
import BlogLayout from '../../components/BlogLayout';

// ─── INFOGRAPHIC 1: Before vs After — A Rep's Morning ─────────────────────────
const BeforeAfterMorning = () => (
    <div className="my-16 not-prose">
        <p className="text-center text-sm font-data uppercase tracking-widest text-[#7B61FF] mb-6">
            Infographic: A Sales Rep's Morning — Before and After ProLine CRM Skills
        </p>
        <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-8">
            <div className="grid md:grid-cols-2 gap-6">
                {/* Before */}
                <div className="bg-[#0A0A14] border border-red-500/15 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-5">
                        <span className="w-2 h-2 rounded-full bg-red-400" />
                        <span className="text-xs font-data uppercase tracking-wider text-red-400">Before — Without the Skill</span>
                    </div>
                    <div className="space-y-3">
                        {[
                            { time: '7:00 AM', task: 'Open ProLine. 4 new leads from yesterday. Start creating records manually.' },
                            { time: '7:25 AM', task: 'Realize you missed a field. Go back. Fix three records.' },
                            { time: '7:40 AM', task: 'Check weather. Three jobs tomorrow look risky. Start calling homeowners.' },
                            { time: '8:15 AM', task: 'One homeowner didn\'t answer. Try again. Reschedule one. Still have two more.' },
                            { time: '8:45 AM', task: 'Write follow-up emails from yesterday\'s inspections. Generic templates. Hope it\'s good enough.' },
                            { time: '9:15 AM', task: 'First actual sales call of the day. Already exhausted.' },
                        ].map((item, i) => (
                            <div key={i} className="flex gap-3 text-xs">
                                <span className="font-data text-red-400 flex-shrink-0 w-16">{item.time}</span>
                                <span className="text-[#F0EFF4]/50 leading-relaxed">{item.task}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-5 pt-4 border-t border-red-500/10">
                        <span className="text-xs text-red-400 font-data">2h 15min of admin before first real work</span>
                    </div>
                </div>
                {/* After */}
                <div className="bg-[#0A0A14] border border-green-500/15 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-5">
                        <span className="w-2 h-2 rounded-full bg-green-400" />
                        <span className="text-xs font-data uppercase tracking-wider text-green-400">After — With the ProLine Skill</span>
                    </div>
                    <div className="space-y-3">
                        {[
                            { time: '5:50 AM', task: 'Agent runs weather check. Flags one job. Drafts homeowner notification automatically.' },
                            { time: '6:00 AM', task: 'Agent creates 4 ProLine records from yesterday\'s leads. Stages set. Contacts linked.' },
                            { time: '6:00 AM', task: 'Agent reads yesterday\'s inspection notes. Drafts 3 context-aware follow-up emails, staged in ProLine.' },
                            { time: '7:00 AM', task: 'Rep opens ProLine. Everything is already done. Reviews the draft emails. Sends two, tweaks one.' },
                            { time: '7:15 AM', task: 'Approves the rain day notification. Sent. Homeowner already has a reschedule offer.' },
                            { time: '7:20 AM', task: 'First sales call of the day. Fully prepared. Not tired.' },
                        ].map((item, i) => (
                            <div key={i} className="flex gap-3 text-xs">
                                <span className="font-data text-green-400 flex-shrink-0 w-16">{item.time}</span>
                                <span className="text-[#F0EFF4]/50 leading-relaxed">{item.task}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-5 pt-4 border-t border-green-500/10">
                        <span className="text-xs text-green-400 font-data">20 min of review. The rest was done before you woke up.</span>
                    </div>
                </div>
            </div>
            <p className="text-center text-xs text-[#F0EFF4]/30 mt-6 italic">
                Same pipeline. Same reps. Same ProLine account. Different morning.
            </p>
        </div>
    </div>
);

// ─── INFOGRAPHIC 2: Capabilities Grid ─────────────────────────────────────────
const CapabilitiesGrid = () => (
    <div className="my-16 not-prose">
        <p className="text-center text-sm font-data uppercase tracking-widest text-[#7B61FF] mb-6">
            Infographic: What the ProLine Skill Actually Does
        </p>
        <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-8">
            <div className="grid md:grid-cols-2 gap-4">
                {[
                    {
                        icon: '📥',
                        title: 'Lead Creation',
                        status: 'green',
                        desc: 'New lead comes in via any channel. Agent creates the ProLine project record and contact automatically — correct stage, source attribution, rep assigned.',
                        note: 'API: edit/project + find/team_member',
                    },
                    {
                        icon: '📅',
                        title: 'Appointment Booking',
                        status: 'green',
                        desc: 'Agent books the inspection with the right rep at the right time, moves the project stage, logs the event. No calendar ping needed.',
                        note: 'API: events/edit + stage update',
                    },
                    {
                        icon: '✉️',
                        title: 'Post-Inspection Email Drafting',
                        status: 'green',
                        desc: 'Agent reads the inspection notes and drafts a context-aware follow-up email — staged inside ProLine, ready for the rep to review and send.',
                        note: 'Reads existing notes before writing — no overwrites',
                    },
                    {
                        icon: '🔍',
                        title: 'Pipeline Audit',
                        status: 'green',
                        desc: '"Which deals have had no activity in 3 days?" Answered in under 30 seconds from the local SQLite mirror. No rate limits, no waiting.',
                        note: 'Local DB query — instant reporting',
                    },
                    {
                        icon: '🌧️',
                        title: 'Rain Day Engine',
                        status: 'green',
                        desc: 'Every morning at 5am, the agent checks a 7-day forecast against every scheduled job. Flags conflicts. Drafts reschedule notifications before your crew leaves.',
                        note: 'Open-Meteo + OpenStreetMap — both free',
                    },
                    {
                        icon: '🔄',
                        title: 'Stage Management',
                        status: 'green',
                        desc: 'Agent moves deals through the pipeline automatically based on what happened — inspection booked, inspection done, estimate sent. Stages update themselves.',
                        note: 'Uses stage IDs, not names — handles the gotcha',
                    },
                    {
                        icon: '🏆',
                        title: 'Deal Quality Scoring',
                        status: 'green',
                        desc: 'Every deal gets an A/B/C/F grade based on notes completeness and process compliance. Weak deals surface themselves before they fall through the cracks.',
                        note: 'Local scoring — no extra API calls',
                    },
                    {
                        icon: '💬',
                        title: 'Messaging via ProLine API',
                        status: 'red',
                        desc: 'Not available at this partner tier. The ProLine activity endpoints for calls and messages require additional access — contact ProLine to request it. The agent drafts; a human sends.',
                        note: 'Known limitation — documented, not hidden',
                    },
                ].map((cap, i) => (
                    <div key={i} className={`p-4 bg-[#0A0A14] rounded-xl border ${
                        cap.status === 'green' ? 'border-[#F0EFF4]/5' : 'border-red-500/15'
                    }`}>
                        <div className="flex items-start gap-3">
                            <div className="text-xl flex-shrink-0 mt-0.5">{cap.icon}</div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-heading font-bold text-[#F0EFF4] text-sm">{cap.title}</span>
                                    {cap.status === 'red' && (
                                        <span className="text-xs px-2 py-0.5 rounded-full font-data bg-red-500/10 border border-red-500/20 text-red-400">
                                            API Limitation
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-[#F0EFF4]/50 leading-relaxed mb-2">{cap.desc}</p>
                                <code className="text-xs text-[#7B61FF]/70 font-data">{cap.note}</code>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <p className="text-center text-xs text-[#F0EFF4]/30 mt-6 italic">
                Every capability listed was verified against a live ProLine partner account.
            </p>
        </div>
    </div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const ProlineSkill = () => {
    return (
        <BlogLayout
            title="What the ProLine Skill Actually Does for Your Roofing Business"
            date="April 21, 2026"
            readTime={8}
            heroImage="/images/blog/proline-skill-hero.png"
            description="The proline-claw skill connects your RoofClaw agent to ProLine CRM. Lead creation, appointment booking, rain day scheduling, pipeline audits — here's what it does in practice, and what it honestly cannot do."
        >
            <p className="lead text-xl text-[#F0EFF4] font-medium">
                Your best reps are spending two hours a day doing data entry.
            </p>
            <p>
                That is not a guess. It is what roofing operations leaders tell us every time we ask. CRM work, follow-up emails, updating stages, checking weather, calling homeowners about rain days — these are not small tasks. They are the first two hours of every morning. Before a single sale has moved forward.
            </p>
            <p>
                The other blog explains how we built the ProLine integration in four hours. This one explains what it actually does for a roofer who runs ProLine every day.
            </p>
            <p>
                If you want the technical story, read that one. If you want to know whether your reps' mornings can look different starting next week, keep reading.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">The Work That Eats Your Best Hours</h2>
            <p>
                Every roofing company that runs ProLine has the same pattern. Leads come in. Someone has to create the record. An inspection gets done. Someone has to update the stage, write the notes, draft the follow-up email. Rain is in the forecast. Someone has to check every scheduled job, figure out which homeowners to call, and start working the phones before the crew shows up at the wrong address.
            </p>
            <p>
                It is important work. It has to be done. But it does not require a skilled sales rep to do it. It requires a system that never forgets, never rushes, and never treats admin as lower priority than the next phone call.
            </p>
            <p>
                That is what the ProLine skill is.
            </p>

            <BeforeAfterMorning />

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">What the Agent Does: Capability by Capability</h2>
            <p>
                Seven capabilities. No fluff. Here is what each one actually looks like in practice.
            </p>

            <h3 className="text-2xl font-heading font-bold mb-4 mt-10">Lead Creation</h3>
            <p>
                A new lead comes in through your website form, a phone call, a referral — any channel. The agent creates the ProLine project record without anyone touching a keyboard. Contact linked, stage set, rep assigned, source logged.
            </p>
            <p>
                Manual entry takes four to six minutes per lead, and it gets skipped when reps are busy. The agent does it in seconds, every time, whether it is 2pm or 2am.
            </p>

            <h3 className="text-2xl font-heading font-bold mb-4 mt-10">Appointment Booking</h3>
            <p>
                The agent books inspections directly in ProLine using the <code className="font-data bg-[#18181B] px-1 py-0.5 rounded text-sm text-[#7B61FF]">events/edit</code> endpoint. It assigns the right rep, moves the project to the correct stage, and logs the appointment. No calendar ping to a human required.
            </p>
            <p>
                The small thing worth knowing: stage changes in ProLine require the actual stage ID, not the stage name. If you pass a string, the API accepts it and does nothing — no error, just silence. The skill handles this correctly. It looks up the ID before writing.
            </p>

            <h3 className="text-2xl font-heading font-bold mb-4 mt-10">Post-Inspection Email Drafting</h3>
            <p>
                After an inspection, the agent reads the notes the rep left in ProLine and drafts a context-aware follow-up email — not a template. The draft lands inside the ProLine record, staged and ready for the rep to review and send with one click.
            </p>
            <p>
                Important note on how this works: the notes field in ProLine is a single text block. Writing to it overwrites whatever was there. The skill always reads the existing notes first, then appends. Nothing gets lost.
            </p>
            <p>
                The email is not generic. If the inspection found significant storm damage, the email says that. If the homeowner mentioned they were planning to sell the house, the email addresses that. The agent uses what was actually written — which is why notes quality matters and why the skill also scores it.
            </p>

            <h3 className="text-2xl font-heading font-bold mb-4 mt-10">Pipeline Audit</h3>
            <p>
                "Which deals haven't had any activity in three days?" is a question most ProLine users can't answer quickly. Running that query against a live API with 40 active projects takes several minutes between rate-limited calls.
            </p>
            <p>
                The skill maintains a local SQLite mirror of the entire pipeline, synced daily. The same question gets answered in under 30 seconds from the local database. Stale deals, stuck stages, missing follow-ups — all visible on demand with no wait.
            </p>

            <h3 className="text-2xl font-heading font-bold mb-4 mt-10">Rain Day Engine</h3>
            <p>
                This is the feature roofers react to most strongly when we show it.
            </p>
            <p>
                Every morning at 5am, the agent pulls a 48-hour forecast for every active job address using Open-Meteo (free API, no subscription). It scores each address against a configurable risk rubric — rain probability, precipitation volume, wind speed, forecast trend since the evening before.
            </p>
            <p>
                The output is a decision for each job: GO (nothing to do), CALL (flag for human decision), or AUTO-CALL (agent drafts the homeowner notification and reschedule offer automatically, no human required). Your crew gets a clean list of what's happening before 6am. No phone scramble at 7:30.
            </p>

            <h3 className="text-2xl font-heading font-bold mb-4 mt-10">Stage Management</h3>
            <p>
                As things happen — inspection booked, inspection completed, estimate sent — the agent moves the deal through the ProLine pipeline automatically. Stages stay current without anyone manually clicking through records.
            </p>
            <p>
                This matters more than it sounds. A pipeline where stages are three steps behind what actually happened is not useful for reporting. The agent keeps it current.
            </p>

            <h3 className="text-2xl font-heading font-bold mb-4 mt-10">Deal Quality Scoring</h3>
            <p>
                Every deal in the pipeline gets an A, B, C, or F grade based on notes completeness and process compliance. Deals with thin notes and skipped steps surface automatically. Before they fall through the cracks, the agent already flagged them.
            </p>
            <p>
                This does two things. First, it identifies which deals need attention. Second, over time, it creates a data feedback loop — reps who write better notes produce better-graded deals, and better-graded deals close at higher rates.
            </p>

            <CapabilitiesGrid />

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">What It Honestly Cannot Do</h2>
            <p>
                No feature list is complete without the honest version.
            </p>
            <p>
                The agent cannot send messages or log calls through the ProLine API at the standard partner tier. The activity endpoints — <code className="font-data bg-[#18181B] px-1 py-0.5 rounded text-sm text-[#7B61FF]">activity/create_message</code>, <code className="font-data bg-[#18181B] px-1 py-0.5 rounded text-sm text-[#7B61FF]">activity/create_call</code> — are disabled by default and require elevated partner access. If you need this capability, contact ProLine directly to request it. Until then, messaging goes through the ProLine UI. The agent drafts. A human sends.
            </p>
            <p>
                Financial fields — quoted value, revenue, job cost — are read-only via the API. The agent can read them for scoring and reporting, but cannot write them. Webhook-based access to financials is on the roadmap; it requires a Make.com pipeline that is designed but not yet live.
            </p>
            <p>
                Contact updates are also blocked. The <code className="font-data bg-[#18181B] px-1 py-0.5 rounded text-sm text-[#7B61FF]">edit/contact</code> endpoint returns "not found" for every valid contact, regardless of credentials. New contacts can be created, but updating existing contact records requires the ProLine UI until this is resolved.
            </p>
            <p>
                These are not hidden limitations discovered after a sale. They are documented, they are known, and the skill is designed to work around them cleanly.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">What It Means for Your Pipeline</h2>
            <p>
                The best ProLine users we talk to all have the same problem: they know the system well, their team uses it consistently, but the CRM is always one step behind reality. Records get created late. Stages stay stale. Follow-up emails go out after a generic two-day delay because nobody had time to write a real one.
            </p>
            <p>
                The skill does not replace ProLine. It operates it. The same records, the same pipeline, the same stages your team already knows — but they stay current, emails go out faster and more relevantly, and rain days get handled before the crew van leaves the yard.
            </p>
            <p>
                If your roofing company runs ProLine and you want to see what this looks like on a live account, the link below is the right next step. No generic demo. An actual conversation about your pipeline and what the agent would do with it.
            </p>

            <hr className="my-16 border-[#F0EFF4]/10" />

            <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[2rem] p-10 relative overflow-hidden not-prose">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#7B61FF] opacity-10 blur-[100px] rounded-full mix-blend-screen pointer-events-none" />
                <h2 className="text-3xl font-heading font-bold mb-4 text-[#F0EFF4]">Add ProLine CRM Skills to Your RoofClaw</h2>
                <p className="text-[#F0EFF4]/70 mb-6 leading-relaxed">
                    If you have a RoofClaw, the ProLine skill is available now. If you don't have one yet, this is a good reason to look at it.
                </p>
                <ul className="space-y-3 mb-8">
                    {[
                        'Lead records created automatically — zero manual entry',
                        'Rain day engine running every morning at 5am',
                        'Post-inspection emails drafted from actual notes, not templates',
                        'Pipeline audit on demand — stale deals surface themselves',
                        'Known API limitations documented so the agent never silently fails',
                    ].map((item, i) => (
                        <li key={i} className="flex gap-3 text-[#F0EFF4]/80 text-sm">
                            <span className="text-[#7B61FF] flex-shrink-0 mt-0.5">→</span>
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
                <a
                    href="/proline-skill"
                    className="inline-flex items-center gap-2 bg-[#7B61FF] hover:bg-[#6B51EF] text-white font-semibold px-8 py-4 rounded-xl transition-colors duration-200"
                >
                    Add ProLine CRM Skills to my RoofClaw →
                </a>
            </div>
        </BlogLayout>
    );
};

export default ProlineSkill;
